import type {
  DateOfInjury,
  FileType,
  ServiceDateOfInjury,
  PaginatedResponse,
} from "types/DataTypes.ts";
import { api } from "./api.ts";

type GetDateOfInjuryArgs = {
  file: string;
};

type GetDateOfInjuryByIdArgs = {
  id: string | number;
};

type PatchDateOfInjuryArgs = {
  id: string;
  data: Partial<DateOfInjury>;
};

type CreateDateOfInjuryArgs = {
  data: { date: string; file: string };
};

type DeleteDateOfInjuryArgs = {
  id: string;
};

type GetServiceDateOfInjuryArgs = {
  msa_id?: string | number;
  cost_projection_id?: string | number;
  document_management_file_id?: string | number;
};

type PatchServiceDateOfInjuryArgs = {
  id: string;
  data: Partial<ServiceDateOfInjury>;
};

type CreateServiceDateOfInjuryArgs = {
  data: {
    date_of_injury: number | string;
    msa?: number | string;
    cost_projection?: number | string;
    document_management_file?: number | string;
  };
};

type DeleteServiceDateOfInjuryArgs = {
  id: string;
};

export const dateOfInjuryAPI = api.injectEndpoints({
  endpoints: (build) => ({
    getDateOfInjury: build.query<
      PaginatedResponse<DateOfInjury>,
      GetDateOfInjuryArgs
    >({
      query: ({ file }) => ({
        url: `api/v1/dates_of_injury/?file=${file}`,
        method: "GET",
      }),
      providesTags: ["DOI"],
    }),
    getDateOfInjuryById: build.query<DateOfInjury, GetDateOfInjuryByIdArgs>({
      query: ({ id }) => ({
        url: `api/v1/dates_of_injury/${id}/`,
        method: "GET",
      }),
      providesTags: ["DOI"],
    }),
    patchDateOfInjuryById: build.mutation<FileType, PatchDateOfInjuryArgs>({
      query: ({ id, data }) => ({
        url: `api/v1/dates_of_injury/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["Files", "DOI"],
    }),
    createDateOfInjuryForFile: build.mutation<FileType, CreateDateOfInjuryArgs>(
      {
        query: ({ data }) => ({
          url: "api/v1/dates_of_injury/",
          method: "POST",
          body: data,
        }),
        invalidatesTags: ["Files", "DOI"],
      },
    ),
    deleteDateOfInjuryForFile: build.mutation<void, DeleteDateOfInjuryArgs>({
      query: ({ id }) => ({
        url: `api/v1/dates_of_injury/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["Files", "DOI"],
    }),
  }),
  overrideExisting: false,
});

export const serviceDateOfInjuryAPI = api.injectEndpoints({
  endpoints: (build) => ({
    getServiceDateOfInjury: build.query<
      PaginatedResponse<ServiceDateOfInjury>,
      GetServiceDateOfInjuryArgs
    >({
      query: ({ msa_id, cost_projection_id, document_management_file_id }) => {
        // build the query params based on which service ID is provided
        const params = new URLSearchParams();
        if (msa_id) params.append("msa_id", msa_id.toString());
        if (cost_projection_id)
          params.append("cost_projection_id", cost_projection_id.toString());
        if (document_management_file_id)
          params.append(
            "document_management_file_id",
            document_management_file_id.toString(),
          );

        return {
          url: `api/v1/service-dates-of-injury/?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: ["ServiceDateOfInjury"],
    }),
    patchServiceDateOfInjuryById: build.mutation<
      ServiceDateOfInjury,
      PatchServiceDateOfInjuryArgs
    >({
      query: ({ id, data }) => ({
        url: `api/v1/service-dates-of-injury/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["ServiceDateOfInjury"],
    }),
    createServiceDateOfInjuryForService: build.mutation<
      ServiceDateOfInjury,
      CreateServiceDateOfInjuryArgs
    >({
      query: ({ data }) => ({
        url: "api/v1/service-dates-of-injury/",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["ServiceDateOfInjury"],
    }),
    deleteServiceDateOfInjury: build.mutation<
      void,
      DeleteServiceDateOfInjuryArgs
    >({
      query: ({ id }) => ({
        url: `api/v1/service-dates-of-injury/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["ServiceDateOfInjury"],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetDateOfInjuryQuery,
  useGetDateOfInjuryByIdQuery,
  usePatchDateOfInjuryByIdMutation,
  useDeleteDateOfInjuryForFileMutation,
  useCreateDateOfInjuryForFileMutation,
} = dateOfInjuryAPI;

export const {
  useGetServiceDateOfInjuryQuery,
  usePatchServiceDateOfInjuryByIdMutation,
  useDeleteServiceDateOfInjuryMutation,
  useCreateServiceDateOfInjuryForServiceMutation,
} = serviceDateOfInjuryAPI;
